import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	ColumnLayout,
	ExpandableSection,
	FormField,
	Select,
	SpaceBetween,
	Spinner,
} from '@amzn/awsui-components-console';
import {
	ValidationDefinition,
	ValidationParameter,
	ValidationType,
} from './configureAttributeStep.types';
import {
	ValidationMetadataSummary,
	getValidationMetadata,
} from './validationMetadataApi';

interface ValidationPanelProps {
	validations: ValidationDefinition[];
	onUpdate: (validations: ValidationDefinition[]) => void;
	operationOptions: { label: string; value: string }[];
	taskInputAttributeIds?: string[];
	validationMetadataSummaries?: ValidationMetadataSummary[];
}

const ReadOnlyField = ({ label, value }: { label: string; value?: React.ReactNode }) => (
	<FormField label={label}>
		<Box>{value === undefined || value === '' || value === null ? '—' : value}</Box>
	</FormField>
);

export default function ValidationPanel({
	validations,
	onUpdate,
	taskInputAttributeIds,
	validationMetadataSummaries,
}: ValidationPanelProps) {
	const [selectError, setSelectError] = useState<string | null>(null);
	const [isFetching, setIsFetching] = useState(false);

	const knownAttributeIds = new Set(taskInputAttributeIds ?? []);
	const isStaleParameter = (param: ValidationParameter): boolean => {
		if (!taskInputAttributeIds) return false;
		if (param.validationParameterType?.inputType !== 'TaskInput') return false;
		const v = param.validationParameterValue;

		return !!v && !knownAttributeIds.has(v);
	};

	const removeValidation = (index: number) => onUpdate(validations.filter((_, i) => i !== index));

	const updateParameterValue = (valIndex: number, paramIndex: number, value: string) => {
		const updated = validations.map((validation, i) => {
			if (i !== valIndex) return validation;
			const params = (validation.validationParameters || []).map((param, j) =>
				j === paramIndex ? { ...param, validationParameterValue: value } : param
			);

			return { ...validation, validationParameters: params };
		});

		onUpdate(updated);
	};

	const attributeOptions = (taskInputAttributeIds ?? []).map((id) => ({ label: id, value: id }));

	const attachedIds = useMemo(
		() => new Set(validations.map((v) => v.validationId).filter(Boolean)),
		[validations]
	);
	const summaryOptions = useMemo(
		() =>
			(validationMetadataSummaries ?? [])
				.filter((s) => !attachedIds.has(s.validationId))
				.map((s) => ({
					label: s.validationName,
					value: s.validationId,
					description: s.validationDescription,
					labelTag: s.validationType,
				})),
		[validationMetadataSummaries, attachedIds]
	);

	const handleSelectValidation = async (validationId: string) => {
		setSelectError(null);
		const summary = (validationMetadataSummaries ?? []).find(
			(s) => s.validationId === validationId
		);

		if (!summary) return;

		setIsFetching(true);
		try {
			const full = await getValidationMetadata(
				summary.validationId,
				summary.validationType as ValidationType
			);

			onUpdate([...validations, full]);
		} catch (error) {
			console.error('Failed to fetch validation metadata:', error);
			setSelectError(
				`Failed to load "${summary.validationName}": ${
					(error as Error)?.message ?? String(error)
				}`
			);
		} finally {
			setIsFetching(false);
		}
	};

	const renderParameter = (valIndex: number, param: ValidationParameter, paramIndex: number) => {
		const stale = isStaleParameter(param);
		const inputType = param.validationParameterType?.inputType ?? '?';
		const target = param.validationParameterValue || '—';
		const baseHeader = param.validationParameterName || `Parameter ${paramIndex + 1}`;
		const headerText = `${baseHeader} → ${target} (${inputType})${
			stale ? '  ⚠ not in task inputs' : ''
		}`;
		const currentValue = param.validationParameterValue ?? '';
		const valueOptions =
			currentValue && !attributeOptions.some((o) => o.value === currentValue)
				? [...attributeOptions, { label: currentValue, value: currentValue }]
				: attributeOptions;

		return (
			<ExpandableSection key={paramIndex} headerText={headerText} variant="default">
				<ColumnLayout columns={2} variant="text-grid">
					<ReadOnlyField label="Name" value={param.validationParameterName} />
					<ReadOnlyField label="Input Type" value={param.validationParameterType?.inputType} />
					<FormField
						label="Value (task attribute)"
						description={stale ? '⚠ not in task inputs' : undefined}
					>
						<Select
							selectedOption={
								currentValue ? { label: currentValue, value: currentValue } : null
							}
							options={valueOptions}
							placeholder="Select a task attribute"
							empty="No task inputs available"
							filteringType="auto"
							onChange={({ detail }) =>
								updateParameterValue(valIndex, paramIndex, detail.selectedOption.value!)
							}
						/>
					</FormField>
					<ReadOnlyField label="Case Handling" value={param.validationParameterCaseHandlingType} />
					<ReadOnlyField label="Data Type" value={param.validationParameterDataType} />
				</ColumnLayout>
			</ExpandableSection>
		);
	};

	const renderValidation = (validation: ValidationDefinition, index: number) => (
		<ExpandableSection
			key={validation.validationId ?? index}
			defaultExpanded
			headerText={`${validation.validationName || `Validation ${index + 1}`}${
				validation.validationType ? ` (${validation.validationType})` : ''
			}`}
			variant="container"
			headerActions={
				<Button variant="icon" iconName="remove" onClick={() => removeValidation(index)} />
			}
		>
			<SpaceBetween size="m">
				<ColumnLayout columns={2} variant="text-grid">
					<ReadOnlyField label="Validation Name" value={validation.validationName} />
					<ReadOnlyField label="Validation Type" value={validation.validationType} />
					<ReadOnlyField label="Status" value={validation.validationStatus} />
					<ReadOnlyField label="Description" value={validation.validationDescription} />
				</ColumnLayout>

				<ExpandableSection defaultExpanded headerText="Validation Config" variant="default">
					<SpaceBetween size="s">
						<ReadOnlyField
							label="Operation ID"
							value={validation.validationOperationConfig?.operationId}
						/>
						<ReadOnlyField
							label="Success Criteria"
							value={(validation.validationOperationConfig?.successCriteria ?? []).join('\n') || undefined}
						/>
						<ReadOnlyField
							label="Failure Criteria"
							value={(validation.validationOperationConfig?.failureCriteria ?? []).join('\n') || undefined}
						/>
					</SpaceBetween>
				</ExpandableSection>

				<ExpandableSection headerText="Parameters" variant="default">
					<SpaceBetween size="s">
						{(validation.validationParameters || []).length === 0 ? (
							<Box color="text-status-inactive">No parameters</Box>
						) : (
							(validation.validationParameters || []).map((param, paramIndex) =>
								renderParameter(index, param, paramIndex)
							)
						)}
					</SpaceBetween>
				</ExpandableSection>
			</SpaceBetween>
		</ExpandableSection>
	);

	return (
		<ExpandableSection defaultExpanded variant="default" headerText="Validation">
			<SpaceBetween size="m">
				{validations.map((v, i) => renderValidation(v, i))}
				<FormField
					label="Add Validation"
					description={
						summaryOptions.length === 0
							? 'No validations available to add'
							: 'Select an existing validation to attach to this task'
					}
					errorText={selectError ?? undefined}
				>
					<SpaceBetween size="xs" direction="horizontal">
						<Select
							selectedOption={null}
							options={summaryOptions}
							placeholder="Select a validation"
							empty="No validations available"
							filteringType="auto"
							disabled={isFetching || summaryOptions.length === 0}
							onChange={({ detail }) =>
								handleSelectValidation(detail.selectedOption.value!)
							}
						/>
						{isFetching && <Spinner />}
					</SpaceBetween>
				</FormField>
			</SpaceBetween>
		</ExpandableSection>
	);
}